import { create } from '@thoughtspot/logger';
import { getRefreshTokenConfig } from '@thoughtspot/session-service';
import { dispatchNonMemoEvent } from '/@utils/event-bridge/event-bridge';
import { EventType } from '../embed-base.util';
import { HeadersManagerType } from './header-manager';
import { RequestPropsType } from './request-interceptor';

const logger = create('browser-fetch-xhr-custom-override');

let commonTokenPromise: Promise<unknown> | null = null;
let refreshInFlight: Promise<void> | null = null;
let tokenReceivedAt = 0;

export const TOKEN_EXPIRY_ERROR_STATUS_CODES = [401, 500, 502];

export const tokenApi = () => {
    if (!commonTokenPromise) {
        commonTokenPromise = dispatchNonMemoEvent(EventType.AuthExpire, {});
        commonTokenPromise
            .then((): null => {
                commonTokenPromise = null;
                return null;
            })
            .catch(() => {
                commonTokenPromise = null;
            });
    }
    return commonTokenPromise;
};

export const getFreshTokenPromise = () => {
    if (!commonTokenPromise) {
        commonTokenPromise = dispatchNonMemoEvent(
            EventType.RefreshAuthToken,
            {},
        ).finally(() => {
            commonTokenPromise = null;
        });
    }
    return commonTokenPromise;
};

const setTokenHeader = (token: string, headersManager: HeadersManagerType) => {
    tokenReceivedAt = Math.floor(Date.now() / 1000);
    headersManager.setHeader('Authorization', `Bearer ${token}`);
    return true;
};

export const refreshToken = async (headersManager: HeadersManagerType) => {
    try {
        const tokenResponse = (await getFreshTokenPromise()) as {
            data: { authToken: string };
        };

        if (tokenResponse?.data?.authToken) {
            return setTokenHeader(tokenResponse.data.authToken, headersManager);
        }
    } catch (err) {
        logger.error('Could not fetch new refresh token ', err);
    }
    return false;
};

export const fetchNewToken = async (headersManager: HeadersManagerType) => {
    try {
        const tokenResponse = (await tokenApi()) as {
            data: { authToken: string };
        };
        if (tokenResponse?.data?.authToken) {
            return setTokenHeader(tokenResponse.data.authToken, headersManager);
        }
    } catch (err) {
        logger.error('Could not fetch new token ', err);
    }
    return false;
};

export const decodeBase64 = (input: string): string | null => {
    try {
        let str = input.replace(/-/g, '+').replace(/_/g, '/');
        const pad = str.length % 4;
        if (pad) {
            str += '='.repeat(4 - pad);
        }
        return atob(str);
    } catch {
        return null;
    }
};

const normalizeTimestamp = (ts: number): number => {
    // If milliseconds, convert to seconds
    return ts >= 1e12 ? Math.floor(ts / 1000) : ts;
};

const extractJwtExpiry = (token: string): number | null => {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = decodeBase64(parts[1]);
    if (!payload) return null;

    try {
        const parsed = JSON.parse(payload);
        if (parsed.exp) {
            return normalizeTimestamp(parsed.exp);
        }
    } catch {
        return null;
    }

    return null;
};

const extractSingleEncodedExpiry = (token: string): number | null => {
    const decoded = decodeBase64(token);
    if (!decoded) return null;

    const parts = decoded.split(':');
    if (parts.length < 3) return null;

    const expiry = Number(parts[1]);
    if (Number.isFinite(expiry)) {
        return normalizeTimestamp(expiry);
    }

    return null;
};

const extractDoubleEncodedExpiry = (token: string): number | null => {
    const firstDecode = decodeBase64(token);
    if (!firstDecode) return null;

    const idx = firstDecode.indexOf(':');
    if (idx === -1) return null;

    const innerEncoded = firstDecode.slice(idx + 1);
    const secondDecode = decodeBase64(innerEncoded);
    if (!secondDecode) return null;

    const parts = secondDecode.split(':');
    if (parts.length < 3) return null;

    const expiry = Number(parts[1]);
    if (Number.isFinite(expiry)) {
        return normalizeTimestamp(expiry);
    }

    return null;
};

export const extractTokenExpiry = (token: string): number | null => {
    // 1. Double encoded
    const doubleEncodedExpiry = extractDoubleEncodedExpiry(token);
    if (doubleEncodedExpiry) return doubleEncodedExpiry;

    // 2. Single encoded
    const singleEncodedExpiry = extractSingleEncodedExpiry(token);
    if (singleEncodedExpiry) return singleEncodedExpiry;

    // 3. JWT
    const jwtExpiry = extractJwtExpiry(token);
    if (jwtExpiry) return jwtExpiry;

    return null;
};

export const shouldRefreshTokenForRequest = (
    input: RequestInfo | URL,
    props: RequestPropsType,
): boolean => {
    const { isEmbedApp, hasAuthToken, embedExpiryInAuthToken } = props || {};
    const shouldRefreshToken =
        (isEmbedApp && hasAuthToken && embedExpiryInAuthToken) || false;
    const { proactiveRefreshApis } = getRefreshTokenConfig();
    return (
        proactiveRefreshApis.some((api: string) =>
            input.toString().includes(api),
        ) && shouldRefreshToken
    );
};

/**
 * Checks if token is near expiry using remaining time and usage ratio (80% threshold)
 */
export const isTokenNearExpiry = (token: string): boolean => {
    const now = Math.floor(Date.now() / 1000);
    const expiry = extractTokenExpiry(token);
    if (!expiry) return true;

    const remaining = expiry - now;
    const {
        usageThreshold = 0.8,
        minRemainingSeconds = 60,
    } = getRefreshTokenConfig();

    // Check if expired or below minimum remaining seconds
    if (remaining <= 0 || remaining <= minRemainingSeconds) {
        return true;
    }

    // Check usage ratio: refresh if token has been used for 80% of its lifetime
    if (tokenReceivedAt > 0) {
        const totalLifetime = expiry - tokenReceivedAt;
        const usedLifetime = now - tokenReceivedAt;
        if (totalLifetime > 0) {
            const usedRatio = usedLifetime / totalLifetime;
            if (usedRatio >= usageThreshold) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Sets the timestamp when token was received (used for usage ratio calculation)
 */
export const setTokenReceivedAt = (timestamp: number): void => {
    tokenReceivedAt = timestamp;
};

/**
 * Ensures token is fresh - refreshes if needed with debouncing to prevent race conditions
 */
export const ensureFreshToken = async (
    headersManager: HeadersManagerType,
): Promise<void> => {
    const headers = headersManager.getHeaders();
    const authHeader = headers?.Authorization;
    if (!authHeader) return;

    const token = authHeader.replace('Bearer ', '');
    if (!isTokenNearExpiry(token)) return;

    // Debounce: if refresh already in progress, wait for it instead of starting a new one
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            try {
                await refreshToken(headersManager);
            } finally {
                refreshInFlight = null;
            }
        })();
    }

    await refreshInFlight;
};
